import { SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';

// Only ever auto-redirect to our own domains — prevents this OAuth-handoff
// forwarding from becoming an open redirect.
function isSafeRedirect(raw: string): boolean {
  try {
    const url = new URL(raw);
    return (
      url.protocol === 'https:' &&
      (url.hostname === 'perihel.io' || url.hostname.endsWith('.perihel.io'))
    );
  } catch {
    return false;
  }
}

const clerkAppearance = {
  elements: {
    rootBox: 'w-full',
    card: 'bg-transparent shadow-none p-0',
    headerTitle: 'text-slate-100',
    headerSubtitle: 'text-slate-400',
    socialButtonsBlockButton:
      'bg-slate-800 border border-slate-700 text-slate-100 hover:bg-slate-700 transition-colors',
    socialButtonsBlockButtonText: 'text-slate-100',
    dividerLine: 'bg-slate-700',
    dividerText: 'text-slate-500',
    formFieldLabel: 'text-slate-300',
    formFieldInput:
      'bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:ring-primary-500/50 focus:border-primary-500/50',
    formButtonPrimary:
      'bg-primary-500 hover:bg-primary-600 text-slate-950 font-semibold shadow-glow',
    footerActionLink: 'text-primary-400 hover:text-primary-300',
    identityPreviewEditButton: 'text-primary-400',
    formFieldAction: 'text-primary-400',
    alert: 'bg-red-500/10 border-red-500/20 text-red-400',
    otpCodeFieldInput: 'bg-slate-800 border-slate-700 text-slate-100',
    formResendCodeLink: 'text-primary-400 hover:text-primary-300',
    identityPreview: 'bg-slate-800 border border-slate-700',
    identityPreviewText: 'text-slate-100',
    identityPreviewEditButtonIcon: 'text-primary-400',
    formHeaderTitle: 'text-slate-100',
    formHeaderSubtitle: 'text-slate-400',
    verificationLinkStatusText: 'text-slate-300',
    verificationLinkStatusIcon: 'text-primary-400',
  },
};

export function AuthPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const { isLoaded, isSignedIn } = useAuth();

  // Clerk's OAuth / Account-Portal flow routes the user through this custom
  // sign-in page with a CROSS-ORIGIN `redirect_url` (the consent page on
  // accounts.<domain>). Clerk's <SignIn> won't perform that cross-origin
  // redirect: forcing it leaves you stuck on /login, and not forcing it sends
  // an already-signed-in user to /dashboard. So once the user is authenticated
  // we perform the redirect ourselves. Guarded to *.perihel.io.
  const rawRedirect = searchParams.get('redirect_url');
  const oauthRedirect = rawRedirect && isSafeRedirect(rawRedirect) ? rawRedirect : null;

  useEffect(() => {
    if (oauthRedirect && isLoaded && isSignedIn) {
      window.location.replace(oauthRedirect);
    }
  }, [oauthRedirect, isLoaded, isSignedIn]);

  // For the not-yet-signed-in OAuth case, return to THIS page after auth
  // (same-origin, which Clerk's forceRedirectUrl accepts) so the effect above
  // then fires the cross-origin hop.
  const loginWithRedirect = oauthRedirect
    ? `/login?redirect_url=${encodeURIComponent(oauthRedirect)}`
    : '/login';
  const signUpUrl = oauthRedirect
    ? `/login?mode=register&redirect_url=${encodeURIComponent(oauthRedirect)}`
    : '/login?mode=register';

  // During an OAuth handoff for an authenticated user (or while auth is still
  // loading), show a redirecting state instead of flashing the sign-in form
  // (which would bounce to /dashboard before our effect runs).
  if (oauthRedirect && (!isLoaded || isSignedIn)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          <p className="text-sm text-slate-400">Continuing sign-in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100">
      {/* Left — brand panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col p-12 relative overflow-hidden">
        {/* Two faint warm bloom moments — the only chrome */}
        <div
          aria-hidden
          className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full blur-3xl pointer-events-none"
          style={{ background: 'rgba(236,124,74,0.10)' }}
        />
        <div
          aria-hidden
          className="absolute bottom-0 right-0 w-[24rem] h-[24rem] rounded-full blur-3xl pointer-events-none"
          style={{ background: 'rgba(184,157,255,0.08)' }}
        />

        {/* Brand mark + name */}
        <div className="relative flex items-center gap-3">
          <img src="/perihelio.svg" alt="" className="w-9 h-9" />
          <span className="font-semibold tracking-tight text-slate-100 text-lg">
            Perihelio
          </span>
        </div>

        {/* Hero — typographic, no animation */}
        <div className="relative my-auto max-w-md">
          <h1 className="font-bold text-[3.25rem] tracking-tighter leading-[1.04] text-slate-100">
            Every account has a moment. Catch it.
          </h1>
          <p className="mt-6 text-slate-400 text-lg leading-relaxed">
            Perihelio tracks the signals — the comment, the reply, the
            meeting — and tells you when an account is at its closest. The
            warm list, updated live.
          </p>

          <ul className="mt-10 space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1 h-1 rounded-full bg-primary-400 shrink-0" />
              <span>Signals decay. Perihelio tracks the curve.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1 h-1 rounded-full bg-primary-400 shrink-0" />
              <span>Closest accounts surface first.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1 h-1 rounded-full bg-primary-400 shrink-0" />
              <span>AI brief: why this account is warm right now.</span>
            </li>
          </ul>
        </div>

        <div className="relative text-xs text-slate-600 tracking-wide">
          © Perihelio · AI-native sales intelligence
        </div>
      </div>

      {/* Right — auth */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden flex flex-col items-center gap-3 mb-10">
            <img src="/perihelio.svg" alt="" className="w-12 h-12" />
            <span className="font-semibold tracking-tight text-slate-100 text-xl">
              Perihelio
            </span>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-slate-100 mb-2">
              {mode === 'login' ? 'Welcome back' : 'Get started'}
            </h2>
            <p className="text-slate-400">
              {mode === 'login'
                ? "Sign in to see who's warm today."
                : "Create your account. See who's warm in minutes."}
            </p>
          </div>

          <div className="clerk-warm">
            {mode === 'login' ? (
              <SignIn
                appearance={clerkAppearance}
                signUpUrl={signUpUrl}
                forceRedirectUrl={oauthRedirect ? loginWithRedirect : '/dashboard'}
              />
            ) : (
              <SignUp
                appearance={clerkAppearance}
                signInUrl={loginWithRedirect}
                forceRedirectUrl={oauthRedirect ? loginWithRedirect : '/onboarding'}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
